import { Divider } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import CreditCardForm from "features/CreditCardForm";
import { FormikProvider, useFormik } from "formik";
import { withStripe, withStripeElements } from "HOCs/withStripe";
import useOpenClose from "hooks/useOpenClose";
import PrimaryButton from "library/Atoms/Button/PrimaryButton";
import ModalLayout from "library/Layouts/ModalLayout";
import OrderSummaryBoxLayout from "library/Layouts/OrderDetailsBoxLayout/OrderSummaryBoxLayout/OrderSummaryBoxLayout";
import { first } from "lodash";
import useOrders from "pages/Dashboard/hooks/useOrders";
import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { retrieveContactTimezone } from "store/features/localization.store";
import { checkoutOrderRequest, paymentRequest } from "store/requests/payment.request";
import { formatDate } from "utility/date-fns";
import { formatPrice } from "utility/helpers/general";
import { getPriceWithVat } from "utility/helpers/orders";
import PaymentFormValidation from "./PaymentForm.validation";

const PaymentForm = ({ onClose, stripeElements, stripe }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const timezone = useSelector(retrieveContactTimezone);

  const [{ selectedOrder }, { isOrderSelected }] = useOrders();

  const [isSessionRefreshing, startSessionRefreshing, stopSessionRefreshing] = useOpenClose(false);

  const paymentForm = useFormik({
    initialValues: {
      card: {
        cards: selectedOrder.payment.cards ?? [],
        expire_at: selectedOrder.payment.expire_at,
        defaultCard: first(selectedOrder.payment.cards)?.id ?? null,
        cardReady: false,
      },
      expressCheckout: false,
    },
    onSubmit: ({ card: { defaultCard }, expressCheckout }, { setSubmitting, setFieldError }) => {
      setSubmitting(true);
      dispatch(
        paymentRequest({
          defaultCard,
          expressCheckout,
          elements: stripeElements,
          stripe,
          checkout: { secret: selectedOrder.payment.secret },
        })
      )
        .then(unwrapResult)
        .then(() => {
          setFieldError("payment", null);
          onClose();
        })
        .catch((error) => setFieldError("payment", error.message))
        .finally(() => setSubmitting(false));
    },
    enableReinitialize: true,
    validationSchema: PaymentFormValidation,
    validateOnMount: true,
  });
  const { setFieldValue, isValid, submitForm: submitPaymentForm, errors, isSubmitting } = paymentForm;

  const {
    totalPrice,
    startingPrice,
    discount: { discount },
    currency,
  } = useMemo(() => {
    return getPriceWithVat(selectedOrder.price, true);
  }, [selectedOrder.price]);

  const shouldShowDiscount = useMemo(() => {
    return isOrderSelected ? discount > 0 : false;
  }, [isOrderSelected, discount]);

  const onToggleCardReady = useCallback((value) => setFieldValue("card.cardReady", value), [setFieldValue]);
  const onSelectSavedCard = useCallback((value) => setFieldValue("card.defaultCard", value), [setFieldValue]);
  const onExpressCheckout = useCallback(() => {
    setFieldValue("expressCheckout", true).then(() => {
      submitPaymentForm();
    });
  }, [setFieldValue, submitPaymentForm]);

  const onReinitializeCheckout = useCallback(() => {
    startSessionRefreshing();

    dispatch(checkoutOrderRequest({ lines: [], orderId: selectedOrder.id }))
      .catch(() => {})
      .finally(() => stopSessionRefreshing());
  }, [dispatch, selectedOrder.id, startSessionRefreshing, stopSessionRefreshing]);

  return (
    <FormikProvider value={paymentForm}>
      <ModalLayout
        className="d-flex flex-column gap-5 pb-2"
        modalControls={
          <PrimaryButton
            id="submit-payment-modal-btn"
            className="w-100"
            label={t("common.buttons.payBtn")}
            onClick={submitPaymentForm}
            loading={isSubmitting}
            disabled={!isValid}
          />
        }
      >
        <OrderSummaryBoxLayout
          referenceSection={selectedOrder.reference}
          dateSection={formatDate(selectedOrder.startDate, timezone)}
          priceSection={
            <Trans
              i18nKey="utils.pricing.inclVAT"
              context={shouldShowDiscount ? "discount" : ""}
              tOptions={{ price: formatPrice(totalPrice, currency), discount: formatPrice(startingPrice, currency) }}
              components={{
                span: <span data-cy={`order-${selectedOrder.reference}-price`} />,
                del: <del className="text-fs-12 text-color-error" />,
                span_discount: <span data-cy={`order-${selectedOrder.reference}-discount`} />,
              }}
              defaults={
                shouldShowDiscount
                  ? `<span>{{price}}</span> <del>(<span_discount>{{discount}}</span_discount>)</del> inc. VAT`
                  : `<span>{{price}}</span> inc. VAT`
              }
            />
          }
        />

        <Divider />

        <CreditCardForm
          name="card"
          onComplete={onToggleCardReady}
          onSelect={onSelectSavedCard}
          onConfirm={onExpressCheckout}
          onRefreshSession={onReinitializeCheckout}
          error={errors.payment}
          isRefreshing={isSessionRefreshing}
        />
      </ModalLayout>
    </FormikProvider>
  );
};

PaymentForm.defaultProps = {
  stripeElements: null,
  stripe: null,
};

PaymentForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  stripeElements: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  stripe: PropTypes.any,
};

export default withStripe(withStripeElements(PaymentForm));
